/**
 * ReportStudio — the Report Generator on the converged studio pattern the
 * Minutes bake-off selected: a document-first canvas with the copilot chat as
 * a FLOATING overlay dock, the config form as a second (mutually exclusive)
 * overlay, and the server DraftSession as the default sync layer.
 *
 * Layout contract is IDENTICAL to MinutesStudio (same top bar, same overlay
 * frames, same resize handles) — the two studios must read as one system.
 * Report-specific differences:
 *   - the document is deterministic-live (report.build), so there is never an
 *     empty state; a tab strip switches Main / CARO / IFC annexures
 *   - "AI Polish" replaces "Generate" (the statutory wording is deterministic;
 *     AI drafts only the optional sections: KAM / EoM / modified-opinion)
 *   - export is copy/txt/html/print + all-annexures (no statutory page ledger)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiMessageSquare, FiZap, FiRefreshCw, FiAlertTriangle, FiSliders, FiX,
} from 'react-icons/fi';
import Button from '../common/Button';
import ExportMenu from '../common/ExportMenu';
import Icon from '../common/Icon';
import { useApp } from '../../context/AppContext';
import { useUI } from '../../context/UIContext';
import { useReportConfig } from '../../context/ReportConfigContext';
import { useDraftSession } from '../../hooks/useDraftSession';
import { useDocumentExport } from '../../hooks/useDocumentExport';
import { aiAPI, draftsAPI } from '../../services/api';
import { saveDraft as cacheSave } from '../../services/draftCacheService';
import { htmlToPlainText, buildStandaloneHtml } from '../../utils/draftUtils';
import { sanitizeDocHtml } from '../../utils/sanitizeHtml';
import { DOC_CSS, DOC_CSS_SCOPED } from '../../utils/docCss';
import { buildDocBasename } from '../../utils/downloadFilename';
import { mergeReportPrefillConfig } from '../../utils/factCarryForward';
import CopilotDock from '../../chat/CopilotDock';
import StudioReportSections from './StudioReportSections';
import { useReportCopilotBridge } from './useReportCopilotBridge';

// docSource → chip spec (line icon + label — app-wide no-emoji rule).
const SOURCE_CHIP = {
  preview: { icon: 'review', label: 'Deterministic', color: '#5a5145', bg: '#f0ebe0' },
  ai: { icon: 'ai', label: 'AI-polished', color: '#145f5f', bg: 'rgba(20,95,95,0.08)' },
  fallback: { icon: 'warning', label: 'AI unavailable', color: '#8a6a1f', bg: 'rgba(176,137,58,0.12)' },
  'user-edited': { icon: 'edit', label: 'Edited by you', color: '#5a5145', bg: '#f0ebe0' },
};

const OVERLAY_SHADOW = 'var(--sh-3, 0 20px 60px rgba(8,17,31,0.18))';
const MIN_OVERLAY_W = 300;
const MAX_OVERLAY_W = 680;
const readWidth = (key, fallback) => {
  try {
    const n = Number(localStorage.getItem(key));
    return Number.isFinite(n) && n >= MIN_OVERLAY_W && n <= MAX_OVERLAY_W ? n : fallback;
  } catch { return fallback; }
};

export default function ReportStudio() {
  const { activeCompany, activeCompanyCin } = useApp();
  const { showToast } = useUI();
  const {
    reportTypeId, config, report, companyContext, companySwitchCount, restoreConfig, updateConfig,
  } = useReportConfig();

  // Synchronous CIN so the cached report paints on the first render instead of
  // after the companies API resolves — see the note in MinutesStudio.
  const { session, conflict, save, takeTheirs, keepMine } = useDraftSession(activeCompanyCin || activeCompany?.cin, 'auditor_report');

  const bodyRef = useRef(null);
  const editingRef = useRef(false);
  const editedTabsRef = useRef(new Set()); // tabs the user hand-edited (frozen from rebuild)
  const lastRenderedRev = useRef(0);
  const restoredRef = useRef(false);
  const [dirty, setDirty] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [currentTab, setCurrentTab] = useState('main');
  const [aiSectionText, setAiSectionText] = useState({});
  const [docSource, setDocSource] = useState('preview');
  // One overlay at a time over the document's right edge.
  const [overlay, setOverlay] = useState('dock'); // 'dock' | 'inspector' | null

  // Deterministic-live document: rebuilt from config on every change, except
  // tabs the user hand-edited (their text is truth until reverted).
  const [tabsHtml, setTabsHtml] = useState(() => report.build(config, companyContext, {}));
  useEffect(() => {
    if (editingRef.current) return; // never rebuild under the caret
    const built = report.build(config, companyContext, aiSectionText);
    setTabsHtml((prev) => {
      const next = {};
      report.tabs.forEach((t) => {
        next[t.id] = editedTabsRef.current.has(t.id)
          ? (prev[t.id] != null ? prev[t.id] : built[t.id])
          : built[t.id];
      });
      return next;
    });
  }, [config, companyContext, aiSectionText, report]);

  const enabledTabs = useMemo(
    () => report.tabs.filter((t) => typeof t.enabled !== 'function' || t.enabled(config)),
    [report, config],
  );
  useEffect(() => {
    if (!enabledTabs.some((t) => t.id === currentTab)) setCurrentTab('main');
  }, [enabledTabs, currentTab]);

  const visibleAiSections = useMemo(
    () => (report.aiSections || []).filter((s) => s.visible(config)),
    [report, config],
  );

  // Origin-aware routing: the AI Agent history reopens report-born chats
  // HERE — make sure the dock is up so the conversation is visible on arrival.
  const location = useLocation();
  const openChatSessionId = location.state?.openChatSessionId || null;
  useEffect(() => {
    if (openChatSessionId) setOverlay('dock');
  }, [openChatSessionId]);

  // "Ask Vee about this" — canvas passage selection → dock quote chip.
  const [selection, setSelection] = useState(null);
  const [quotedSelection, setQuotedSelection] = useState(null);
  const handleCanvasMouseUp = () => {
    const sel = window.getSelection();
    const text = sel ? String(sel.toString()).trim() : '';
    if (!text || !bodyRef.current || !sel.rangeCount || !bodyRef.current.contains(sel.anchorNode)) {
      setSelection(null);
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setSelection({ text: text.slice(0, 1200), x: rect.left + rect.width / 2, y: rect.top });
  };
  const quoteIntoDock = () => {
    if (!selection) return;
    setQuotedSelection(selection.text);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    setOverlay('dock');
  };

  // ── Resizable overlays (same interaction + persistence as MinutesStudio) ──
  const [dockW, setDockW] = useState(() => readWidth('cs_report_dock_w', 380));
  const [inspW, setInspW] = useState(() => readWidth('cs_report_insp_w', 330));
  const resizing = useRef(null);
  const startResize = (which) => (e) => {
    e.preventDefault();
    resizing.current = { which, startX: e.clientX, baseW: which === 'dock' ? dockW : inspW };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    const move = (ev) => {
      const r = resizing.current;
      if (!r) return;
      const w = Math.min(MAX_OVERLAY_W, Math.max(MIN_OVERLAY_W, r.baseW + (r.startX - ev.clientX)));
      if (r.which === 'dock') setDockW(w); else setInspW(w);
    };
    const up = () => {
      const r = resizing.current;
      resizing.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      try {
        if (r) {
          if (r.which === 'dock') setDockW((w) => { localStorage.setItem('cs_report_dock_w', String(w)); return w; });
          else setInspW((w) => { localStorage.setItem('cs_report_insp_w', String(w)); return w; });
        }
      } catch { /* ignore */ }
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 1100px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1100px)');
    const onChange = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const overlayWidth = overlay === 'dock' ? dockW : inspW;
  const overlayFrame = narrow
    ? { className: 'top-0 right-0 bottom-0 max-w-[94%]', style: { width: 420, borderRadius: 'var(--r-xl, 16px) 0 0 var(--r-xl, 16px)' } }
    : { className: 'top-4 right-4 bottom-4 max-w-[92%]', style: { width: overlayWidth, borderRadius: 'var(--r-xl, 16px)' } };

  const effectiveSource = dirty || editedTabsRef.current.size ? 'user-edited' : docSource;

  const bridge = useReportCopilotBridge({
    // Both versions, same as the Minutes dock: the live combined document, and
    // the version the user's hand-edits were made against. Here the baseline is
    // recomputed rather than snapshotted — the report is a deterministic build
    // from (config, company, AI sections), so re-running that build with the
    // current inputs reproduces exactly the document the edits landed on top of.
    getDocState: () => ({
      docSource: effectiveSource,
      hasUserEdits: editedTabsRef.current.size > 0,
      currentHtml: combinedHtml(),
      // joinTabs, NOT combinedHtml — the latter splices the live DOM over the
      // current tab, which would fold the user's edits into the very version
      // they are supposed to be compared against.
      baselineHtml: editedTabsRef.current.size ? joinTabs(report.build(config, companyContext, aiSectionText)) : '',
    }),
    isInspectorOpen: () => overlay === 'inspector',
    // Vee rewriting a narrative section (Basis for Modified Opinion, Emphasis
    // of Matter, or the CARO 3(vii)(b) disputed-dues detail on Annexure A)
    // feeds the SAME deterministic rebuild the form does — merged, never
    // replaced, so patching one section can't wipe the others.
    applyAiSections: (sections) => {
      setAiSectionText((prev) => ({ ...prev, ...sections }));
      setDocSource((prev) => (prev === 'user-edited' ? prev : 'ai'));
    },
    // Per-clause CARO comment overrides. They live in the CONFIG, so the
    // deterministic rebuild renders them and they survive every later change —
    // but a tab the user has hand-edited is FROZEN from that rebuild, which
    // would leave the new clause comment invisible on the very tab it belongs
    // to. Unfreeze Annexure A so the change actually reaches the screen; the
    // user's edits to the OTHER tabs are untouched.
    applyCaroClauses: async (clauses) => {
      updateConfig('caroClauses', clauses);
      if (editedTabsRef.current.delete('caro')) {
        showToast('Your hand-edits to Annexure A were replaced by the updated clause', 'info');
      }
      const source = editedTabsRef.current.size ? 'user-edited' : 'ai';
      setDocSource(source);
      // Build + persist here rather than waiting for the config effect: the
      // rebuild alone only repaints, and an unsynced clause comment would be
      // lost to the next session load (config lives in sessionStorage, the
      // shared truth lives in the DraftSession).
      const nextConfig = { ...config, caroClauses: clauses };
      const built = report.build(nextConfig, companyContext, aiSectionText);
      // Rebuild Annexure A, but keep whatever the user hand-edited on the OTHER
      // tabs — the same tab-by-tab rule the config effect follows, applied to
      // what gets persisted too, so the saved copy is the copy on screen.
      const nextTabs = { ...tabsHtml, ...built };
      editedTabsRef.current.forEach((id) => { nextTabs[id] = tabsHtml[id]; });
      setTabsHtml(nextTabs);
      persistToHistory(nextTabs, source, nextConfig);
      await syncSession(nextTabs, source, nextConfig);
    },
  });

  const fileBase = buildDocBasename({
    company: config.companyName || companyContext?.name || activeCompany?.name || 'Company',
    docType: report.shortLabel || report.label || 'Report',
  });

  const {
    copy: copyDoc, downloadTxt, downloadHtml, print: printDoc,
  } = useDocumentExport(bodyRef, {
    title: `${report.shortLabel || 'Report'} - ${config.companyName || companyContext?.name || 'Company'}`,
    filename: fileBase,
    fallbackHtml: tabsHtml[currentTab] || '',
    onNotify: showToast,
    audit: { resourceType: 'report', resourceId: `report_${reportTypeId}`, surface: 'report-studio' },
  });

  // Enabled annexures, in order, page-broken between them. Pure — exactly the
  // tabs handed in, with nothing spliced from the DOM.
  const joinTabs = (tabs) => enabledTabs
    .map((t, i) => (i ? '<div style="page-break-before:always"></div>' : '') + (tabs[t.id] || ''))
    .join('\n');

  // Combined document (all enabled annexures) — the sync + export body. Takes
  // the live DOM for the current tab so uncommitted edits are never lost.
  const combinedHtml = (tabs = tabsHtml) => {
    const merged = { ...tabs };
    if (bodyRef.current) merged[currentTab] = bodyRef.current.innerHTML;
    return joinTabs(merged);
  };

  // ── Canvas render (ref-driven innerHTML — contentEditable rule) ──────────
  const lastCommittedHtmlRef = useRef('');
  const renderDoc = (html) => {
    if (!bodyRef.current) return;
    bodyRef.current.innerHTML = sanitizeDocHtml(html || '');
    lastCommittedHtmlRef.current = sanitizeDocHtml(bodyRef.current.innerHTML);
  };
  useEffect(() => {
    if (editingRef.current) return; // protect the caret
    renderDoc(tabsHtml[currentTab] || '');
    setDirty(false);
  }, [tabsHtml, currentTab]);

  // ── Persistence: DraftSession (chat sync) + My Documents (history) ───────
  // `cfg` is explicit because a caller that has JUST changed the config cannot
  // read the change back from context in the same tick — snapshotting the stale
  // `config` there would sync a document whose clause overrides the saved config
  // does not contain.
  const sessionSnapshot = (tabs, source, cfg = config) => ({
    kind: 'report',
    reportTypeId,
    reportConfig: cfg,
    tabsHtml: tabs,
    aiSectionText,
    editedTabs: [...editedTabsRef.current],
    docSource: source,
  });

  const persistToHistory = (tabs, source, cfg = config) => {
    const combined = joinTabs(tabs);
    const record = {
      documentId: `report_${reportTypeId}`,
      companyCin: config.cin || companyContext?.cin || activeCompany?.cin || '',
      companyName: config.companyName || companyContext?.name || activeCompany?.name || '',
      documentTitle: `${report.label} — ${config.companyName || companyContext?.name || 'Company'}`,
      documentType: 'report',
      pageOrigin: 'report-generator',
      docSource: source,
      docHtml: `<style>${DOC_CSS}</style>${combined}`,
      minutesConfig: sessionSnapshot(tabs, source, cfg),
      draftMeta: {},
    };
    cacheSave(record).catch(() => {});
    draftsAPI.save(record).catch(() => {});
  };

  const syncSession = async (tabs, source, cfg = config) => {
    const combined = enabledTabs
      .map((t, i) => (i ? '<div style="page-break-before:always"></div>' : '') + (tabs[t.id] || ''))
      .join('\n');
    const res = await save({
      config: sessionSnapshot(tabs, source, cfg),
      docHtml: combined,
      docPlainText: htmlToPlainText(combined),
      docSource: source,
    }).catch(() => null);
    if (res?.session) lastRenderedRev.current = res.session.rev;
    else if (res?.conflict) showToast('This report was updated elsewhere — resolve the conflict below', 'warning');
    return res;
  };

  // Server → studio (another device / an older visit): adopt the session's
  // config + AI sections + edited tabs; the deterministic rebuild re-renders.
  useEffect(() => {
    if (!session) return;
    if (session.rev === lastRenderedRev.current) return;
    if (editingRef.current) return;
    const snap = session.config;
    if (snap && snap.kind === 'report') {
      restoreConfig({ reportTypeId: snap.reportTypeId, reportConfig: snap.reportConfig });
      setAiSectionText(snap.aiSectionText || {});
      editedTabsRef.current = new Set(snap.editedTabs || []);
      if (snap.tabsHtml) setTabsHtml(snap.tabsHtml);
      setDocSource(snap.docSource || session.docSource || 'preview');
    }
    lastRenderedRev.current = session.rev;
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Company switch → back to a fresh deterministic document for the new
  // company (its own session, if any, arrives via useDraftSession).
  const prevSwitchRef = useRef(companySwitchCount);
  useEffect(() => {
    if (companySwitchCount > prevSwitchRef.current) {
      prevSwitchRef.current = companySwitchCount;
      setAiSectionText({});
      // Clause overrides are AUDIT FINDINGS about one company. The context's
      // company-sync only rewrites company-derived fields, so without this they
      // would silently follow the user into the next company's report.
      if (Object.keys(config.caroClauses || {}).length) updateConfig('caroClauses', {});
      editedTabsRef.current = new Set();
      setDocSource('preview');
      lastRenderedRev.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySwitchCount]);

  // ── Chat handoff ("Open in Report Generator" from the AI Agent page) ─────
  useEffect(() => {
    const prefill = location.state?.reportPrefill;
    if (!prefill || restoredRef.current) return;
    restoredRef.current = true;
    // Same carry-forward as handleDockArtifact: the prefill is a tool echo, so
    // '' means "not restated" — never let it erase facts the studio holds.
    const sameCompany = !prefill.reportConfig?.cin || !config.cin
      || String(prefill.reportConfig.cin).toUpperCase() === String(config.cin).toUpperCase();
    restoreConfig({
      reportTypeId: prefill.reportTypeId,
      reportConfig: sameCompany ? mergeReportPrefillConfig(config, prefill.reportConfig) : prefill.reportConfig,
    });
    setAiSectionText(prefill.aiSectionText || {});
    editedTabsRef.current = new Set();
    const src = String(prefill.docSource || '');
    setDocSource(/fallback/i.test(src) ? 'fallback' : src === 'preview' ? 'preview' : 'ai');
    showToast('Report opened from AI chat — review the config, Vee is right here', 'success');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.reportPrefill]);

  // ── My Documents restore (draftId in route state) ─────────────────────────
  useEffect(() => {
    const draftId = location.state?.draftId;
    if (!draftId || restoredRef.current) return;
    restoredRef.current = true;
    draftsAPI.getById(draftId).then((res) => {
      const draft = res?.data;
      const mc = draft?.minutesConfig;
      if (!draft || !mc || mc.kind !== 'report') return;
      restoreConfig({ reportTypeId: mc.reportTypeId, reportConfig: mc.reportConfig });
      if (mc.aiSectionText) setAiSectionText(mc.aiSectionText);
      if (mc.tabsHtml) setTabsHtml(mc.tabsHtml);
      editedTabsRef.current = new Set(mc.editedTabs || []);
      setDocSource(draft.docSource || mc.docSource || 'preview');
      showToast('Restored saved report draft', 'success');
    }).catch(() => { /* silent — proceed with the live build */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.draftId]);

  // ── Hand-edits: commit on click-away (mistake-proof baseline compare) ─────
  const commitEdits = async () => {
    editingRef.current = false;
    if (!dirty) return;
    const html = sanitizeDocHtml(bodyRef.current?.innerHTML || '');
    if (html === lastCommittedHtmlRef.current) {
      setDirty(false); // caret-only interaction — nothing actually changed
      return;
    }
    editedTabsRef.current.add(currentTab);
    const tabs = { ...tabsHtml, [currentTab]: html };
    setTabsHtml(tabs);
    lastCommittedHtmlRef.current = html;
    setDirty(false);
    setDocSource('user-edited');
    persistToHistory(tabs, 'user-edited');
    await syncSession(tabs, 'user-edited');
  };

  // Escape hatch: drop every hand-edit; the deterministic build (with the
  // current AI sections) becomes the document again.
  const discardMyEdits = async () => {
    editedTabsRef.current = new Set();
    const built = report.build(config, companyContext, aiSectionText);
    setTabsHtml(built);
    const source = Object.keys(aiSectionText).length ? 'ai' : 'preview';
    setDocSource(source);
    setDirty(false);
    await syncSession(built, source);
    showToast('Your edits were discarded — back to the generated report', 'success');
  };

  const switchTab = (tabId) => {
    if (tabId === currentTab) return;
    if (dirty && bodyRef.current) {
      // Capture uncommitted edits before the tab swaps the canvas content.
      const html = sanitizeDocHtml(bodyRef.current.innerHTML);
      if (html !== lastCommittedHtmlRef.current) {
        editedTabsRef.current.add(currentTab);
        setTabsHtml((prev) => ({ ...prev, [currentTab]: html }));
        setDocSource('user-edited');
      }
      setDirty(false);
    }
    editingRef.current = false;
    setCurrentTab(tabId);
  };

  // ── Dock artifact (Vee drafted/polished the report in chat) ──────────────
  const handleDockArtifact = async (draft) => {
    if (!draft || draft.kind !== 'report') {
      if (draft) showToast('Open non-report drafts from the Vee page', 'info');
      return;
    }
    const pf = draft.reportPrefill;
    if (!pf) return;
    // Fact carry-forward: the tool's reportConfig echo carries '' for every
    // fact the model didn't restate (fyEnded, signature block…) — a scoped
    // edit must not regress those to placeholders. Merge over the live config
    // (same company only; facts must never leak across workspaces), and keep
    // previously drafted AI sections that this call didn't re-draft.
    const sameCompany = !draft.companyCin || !config.cin
      || String(draft.companyCin).toUpperCase() === String(config.cin).toUpperCase();
    const mergedConfig = sameCompany ? mergeReportPrefillConfig(config, pf.reportConfig) : pf.reportConfig;
    const mergedSections = sameCompany ? { ...aiSectionText, ...(pf.aiSectionText || {}) } : (pf.aiSectionText || {});
    restoreConfig({ reportTypeId: pf.reportTypeId, reportConfig: mergedConfig });
    setAiSectionText(mergedSections);
    editedTabsRef.current = new Set();
    const src = String(pf.docSource || draft.source || '');
    const source = /fallback/i.test(src) ? 'fallback' : src === 'preview' ? 'preview' : 'ai';
    setDocSource(source);
    setDirty(false);
    const built = report.build({ ...report.defaultConfig, ...mergedConfig }, companyContext, mergedSections);
    setTabsHtml(built);
    if (source !== 'preview') persistToHistory(built, source);
    await syncSession(built, source);
  };

  // ── AI polish (drafts KAM / EoM / modified-opinion sections) ─────────────
  const handleAIPolish = async () => {
    if (aiBusy) return;
    if (!visibleAiSections.length) {
      showToast('Nothing to polish yet — enable Key Audit Matters / Emphasis of Matter, or choose a modified opinion', 'info');
      return;
    }
    setAiBusy(true);
    try {
      const payload = {
        reportTypeId,
        sections: visibleAiSections.map((s) => s.id),
        companyType: config.companyType,
        framework: config.framework,
        opinionType: config.opinionType,
        config,
      };
      const res = await aiAPI.draftAuditorReportSections(payload, companyContext, { useCache: false });
      if (!res?.success || !res.data) throw new Error(res?.message || 'AI polish failed');
      const source = res.data.source;
      const sections = res.data.sections || {};
      const isFallback = source === 'fallback' || source === 'ai-fallback-normalized';
      const hasContent = Boolean(
        (Array.isArray(sections.kam) && sections.kam.length)
        || (sections.emphasis && sections.emphasis.trim())
        || (sections.disputedDues && sections.disputedDues.trim())
        || (sections.basisModified && sections.basisModified.trim()),
      );
      if (!isFallback && hasContent) {
        editedTabsRef.current = new Set(); // AI polish supersedes prior edits
        setAiSectionText(sections);
        setDocSource('ai');
        const built = report.build(config, companyContext, sections);
        setTabsHtml(built);
        persistToHistory(built, 'ai');
        await syncSession(built, 'ai');
        showToast('AI polish applied', 'success');
      } else {
        setDocSource('fallback');
        showToast('AI unavailable — showing the deterministic report', 'warning');
      }
    } catch (e) {
      setDocSource('fallback');
      showToast(e.message || 'AI polish failed — showing the deterministic report', 'error');
    } finally {
      setAiBusy(false);
    }
  };

  const handleExportAll = () => {
    const combined = combinedHtml();
    const title = `${report.label} — ${config.companyName || companyContext?.name || 'Company'}`;
    const base = buildDocBasename({
      company: config.companyName || companyContext?.name,
      docType: `${report.label || 'Report'} all annexures`,
    });
    const blob = new Blob([buildStandaloneHtml(title, combined)], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${base}.html`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('Exported all annexures as HTML', 'success');
  };

  // ── "Let Vee fill the gaps" (report twin of the Minutes card) ────────────
  const GAP_FIELDS = [
    ['fyEnded', 'financial year ended'],
    ['firmName', 'audit firm name'],
    ['frn', 'firm registration number'],
    ['partnerName', 'engagement partner'],
    ['membershipNo', 'membership number'],
    ['udin', 'UDIN'],
    ['place', 'place of signature'],
    ['reportDate', 'date of the report'],
  ];
  const missingFields = GAP_FIELDS
    .filter(([k]) => !String(config[k] || '').trim())
    .map(([, label]) => label);

  const [autoSend, setAutoSend] = useState(null);

  // Conflict banner's smart third way (see MinutesStudio.letVeeMerge).
  const letVeeMerge = async () => {
    const combined = combinedHtml();
    const res = await keepMine({
      config: sessionSnapshot({ ...tabsHtml, [currentTab]: bodyRef.current?.innerHTML || tabsHtml[currentTab] }, 'user-edited'),
      docHtml: combined,
      docPlainText: htmlToPlainText(combined),
      docSource: 'user-edited',
    });
    if (!res?.session) return; // still conflicted — the banner stays up
    setOverlay('dock');
    setAutoSend({
      text: 'You updated this report while I was hand-editing it, so there were two versions. '
        + 'I have kept MY edited version as the working draft. Using targeted edits, re-apply what your update '
        + 'changed on top of my version — but never overwrite any of my manual edits. Where the two conflict, '
        + 'keep mine and tell me exactly what you skipped.',
    });
  };

  const askVeeToFillGaps = () => {
    setOverlay('dock');
    setAutoSend({
      text: `Fill in what you can on the report form: ${missingFields.join(', ')}.\n\n`
        + 'Use the company record where it is authoritative (statutory auditor firm, registered office city as place of signature) '
        + 'and anything I have already told you in this chat. For anything you cannot source that way — the UDIN, the report date, '
        + 'engagement-partner details you do not know — ask me instead of guessing; leave the field blank until I answer.',
    });
  };

  const chip = SOURCE_CHIP[effectiveSource] || SOURCE_CHIP.preview;

  const exportItems = [
    { key: 'copy', label: 'Copy text', onClick: copyDoc },
    { key: 'txt', label: 'Plain text (.txt)', onClick: downloadTxt },
    { key: 'html', label: 'Web page (.html)', onClick: downloadHtml },
    { key: 'print', label: 'Print / PDF', hint: 'Browser print dialog — save as a plain PDF', onClick: printDoc },
    {
      key: 'all',
      label: 'All annexures (.html)',
      hint: 'Main report + every enabled annexure, one file',
      onClick: handleExportAll,
      dividerBefore: true,
    },
  ];

  return (
    // One rounded surface for the whole studio — same silhouette as the
    // Minutes Studio so the two read as one system.
    <div
      className="flex flex-col h-full min-h-0 bg-white border overflow-hidden"
      style={{ borderColor: '#e7e0d0', borderRadius: 'var(--r-xl, 16px)', boxShadow: 'var(--sh-1, 0 2px 12px rgba(8,17,31,0.04))' }}
    >
      {/* ── Top bar — identical chrome to MinutesStudio ── */}
      <div className="flex items-center gap-3 px-4 h-[54px] border-b bg-white shrink-0" style={{ borderColor: '#e7e0d0' }}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[13.5px] font-bold text-[#08111f] truncate" style={{ letterSpacing: '0.1px' }}>
              {config.companyName || activeCompany?.name || 'Select a company to start'}
            </span>
            <span
              className="shrink-0 inline-flex items-center gap-1 h-[20px] px-2 text-[9.5px] font-semibold rounded-full"
              style={{ color: chip.color, background: chip.bg }}
            >
              <Icon name={chip.icon} size={11} />
              {chip.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] leading-tight truncate mt-[1px]" style={{ color: '#9a9080' }}>
            <span className="font-semibold uppercase tracking-[0.5px] text-[9px]">Report Studio</span>
            <span aria-hidden="true">·</span>
            {session ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: dirty ? '#b0893a' : '#3f8f5f' }} aria-hidden="true" />
                {dirty ? 'Unsaved edits — sync on click-away' : `Synced with Vee · v${session.rev}`}
                {effectiveSource === 'user-edited' && (
                  <button
                    type="button"
                    onClick={discardMyEdits}
                    title="Back to the generated report — throws away every hand-edit"
                    className="underline decoration-dotted underline-offset-2 hover:decoration-solid"
                    style={{ color: '#145f5f' }}
                  >
                    Discard my edits
                  </button>
                )}
              </span>
            ) : (
              <span>{report.label} · live deterministic build</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Overlay toggle group — one segmented surface, active = teal */}
          <div className="flex items-center gap-0.5 rounded-xl border p-0.5" style={{ borderColor: '#e2d9c8', background: '#faf8f2' }}>
            {[
              ['dock', 'Ask Vee', <FiMessageSquare size={13} key="i" />],
              ['inspector', 'Details', <FiSliders size={13} key="i" />],
            ].map(([key, label, icon]) => (
              <button
                key={key}
                type="button"
                aria-pressed={overlay === key}
                onClick={() => setOverlay((o) => (o === key ? null : key))}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[10px] text-[12px] font-semibold transition-colors active:scale-95"
                style={overlay === key
                  ? { background: '#145f5f', color: '#fff', boxShadow: 'var(--sh-1, 0 2px 12px rgba(8,17,31,0.04))' }
                  : { color: '#5a5145' }}
              >
                {icon} {label}
              </button>
            ))}
          </div>

          <div className="w-px h-6" style={{ background: '#e7e0d0' }} aria-hidden="true" />

          <ExportMenu size="sm" variant="outline" items={exportItems} />
          {/* Deliberately SECONDARY: Vee is the drafting path; this runs the
              optional AI sections (KAM / EoM / modified opinion) directly. */}
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FiZap size={13} />}
            onClick={handleAIPolish}
            loading={aiBusy}
            disabled={aiBusy || !activeCompany}
            title="AI-draft the optional sections — Vee does the same thing in chat"
          >
            AI Polish
          </Button>
        </div>
      </div>

      {/* ── Conflict banner ── */}
      {conflict && (
        <div className="flex items-center gap-3 px-4 py-2 border-b flex-wrap shrink-0" style={{ background: '#fdf4e7', borderColor: '#f0e3c5' }}>
          <FiAlertTriangle size={14} className="shrink-0" style={{ color: '#8a6a1f' }} />
          <span className="text-[12px]" style={{ color: '#5a5145' }}>This report was updated elsewhere while you were editing. Which version should win?</span>
          <span className="ml-auto flex items-center gap-2">
            <Button size="xs" variant="ghost" leftIcon={<FiRefreshCw size={12} />} onClick={() => { takeTheirs(); lastRenderedRev.current = 0; }}>
              Take the other version
            </Button>
            <Button size="xs" variant="ghost" onClick={() => {
              const combined = combinedHtml();
              keepMine({
                config: sessionSnapshot({ ...tabsHtml, [currentTab]: bodyRef.current?.innerHTML || tabsHtml[currentTab] }, 'user-edited'),
                docHtml: combined,
                docPlainText: htmlToPlainText(combined),
                docSource: 'user-edited',
              });
            }}>
              Keep mine
            </Button>
            {/* The smart default: neither version is discarded. */}
            <Button size="xs" leftIcon={<Icon name="ai" size={12} />} onClick={letVeeMerge} title="Keep your edits and have Vee re-apply its update around them">
              Let Vee merge both
            </Button>
          </span>
        </div>
      )}

      {/* ── Document canvas (full width) + overlays ── */}
      <div className="flex-1 min-h-0 relative">
        <div
          className="absolute inset-0 overflow-y-auto cs-content-scroll"
          style={{
            background: '#f3efe5',
            paddingRight: narrow || !overlay ? 0 : overlayWidth + 16,
            transition: resizing.current ? 'none' : 'padding-right var(--dur-base, 200ms) ease',
          }}
        >
          <div className="max-w-[760px] mx-auto px-4 py-6">
            {/* Annexure tab strip — same pill language as the top-bar toggle */}
            {enabledTabs.length > 1 && (
              <div className="flex items-center gap-0.5 rounded-xl border p-0.5 mb-3 w-fit mx-auto bg-white" style={{ borderColor: '#e2d9c8' }}>
                {enabledTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={currentTab === t.id}
                    onClick={() => switchTab(t.id)}
                    className="h-7 px-3 rounded-[10px] text-[11.5px] font-semibold transition-colors active:scale-95"
                    style={currentTab === t.id
                      ? { background: '#145f5f', color: '#fff' }
                      : { color: '#5a5145' }}
                  >
                    {String(t.label).replace(/^[^\w]*\s*/, '')}
                  </button>
                ))}
              </div>
            )}

            <div
              className="bg-white overflow-hidden border"
              style={{ borderColor: 'rgba(226,217,200,0.8)', borderRadius: 'var(--r-xl, 16px)', boxShadow: 'var(--sh-panel, 0 10px 30px -10px rgba(122,112,96,0.08))' }}
              onMouseUp={handleCanvasMouseUp}
            >
              <style>{DOC_CSS_SCOPED}</style>
              <div
                ref={bodyRef}
                className="doc-preview"
                contentEditable
                suppressContentEditableWarning
                onFocus={() => { editingRef.current = true; }}
                onInput={() => setDirty(true)}
                onBlur={commitEdits}
                style={{ fontFamily: "'Times New Roman', Times, serif", fontSize: '11.5pt', lineHeight: 1.7, color: '#000', padding: '42px 46px', outline: 'none', minHeight: '420px' }}
              />
            </div>
            <div className="text-[10.5px] text-center mt-3" style={{ color: '#9a9080' }}>
              Click anywhere in the document to edit — Vee sees your edits on its next change.
            </div>
          </div>
        </div>

        {/* Floating "Ask Vee about this" over the current document selection */}
        {selection && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={quoteIntoDock}
            className="fixed z-[70] inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-semibold text-white shadow-lg transition-transform active:scale-95"
            style={{ background: '#145f5f', left: Math.max(12, selection.x - 70), top: Math.max(12, selection.y - 40) }}
          >
            <FiMessageSquare size={12} /> Ask Vee about this
          </button>
        )}

        {/* Floating chat dock */}
        {overlay === 'dock' && (
          <div
            className={`absolute ${overlayFrame.className} z-30 ${resizing.current ? '' : 'animate-in slide-in-from-right duration-200'}`}
            style={{ ...overlayFrame.style, boxShadow: OVERLAY_SHADOW, border: '1px solid #e2d9c8', background: '#fff' }}
          >
            {!narrow && (
              <div
                onMouseDown={startResize('dock')}
                className="absolute -left-1.5 top-0 bottom-0 w-3 cursor-col-resize flex items-center justify-center z-20 group/rz"
                aria-hidden="true"
                title="Drag to resize"
              >
                <div className="w-[3px] h-14 rounded-full bg-[#ddd6c6] group-hover/rz:bg-[#145f5f] transition-colors" />
              </div>
            )}
            <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 'inherit' }}>
              <CopilotDock
                getPageContext={bridge.getPageContext}
                onClientActions={bridge.handleClientActions}
                onArtifact={handleDockArtifact}
                onClose={() => setOverlay(null)}
                historyDraftKind="report"
                openSessionId={openChatSessionId}
                quote={quotedSelection}
                onClearQuote={() => setQuotedSelection(null)}
                autoSend={autoSend}
                onAutoSent={() => setAutoSend(null)}
                emptyHint={(
                  <>
                    Tell Vee about the engagement in plain words — <i>“statutory audit for FY ended 31 March
                    2026, qualified opinion over unconfirmed trade receivables”</i> — the form fills itself,
                    and Vee drafts the report right here when you say so.
                  </>
                )}
                composerPlaceholder="Describe the engagement…"
              />
            </div>
          </div>
        )}

        {/* Floating inspector */}
        {overlay === 'inspector' && (
          <div
            className={`absolute ${overlayFrame.className} z-30 border ${resizing.current ? '' : 'animate-in slide-in-from-right duration-200'}`}
            style={{ ...overlayFrame.style, boxShadow: OVERLAY_SHADOW, borderColor: '#e2d9c8', background: '#faf8f2' }}
          >
            {!narrow && (
              <div
                onMouseDown={startResize('inspector')}
                className="absolute -left-1.5 top-0 bottom-0 w-3 cursor-col-resize flex items-center justify-center z-20 group/rz"
                aria-hidden="true"
                title="Drag to resize"
              >
                <div className="w-[3px] h-14 rounded-full bg-[#ddd6c6] group-hover/rz:bg-[#145f5f] transition-colors" />
              </div>
            )}
            <div className="absolute inset-0 flex flex-col" style={{ borderRadius: 'inherit' }}>
              <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0" style={{ borderColor: '#e7e0d0', background: '#faf8f2' }}>
                <FiSliders size={13} style={{ color: '#145f5f' }} />
                <span className="text-[12px] font-bold text-[#08111f]">Report details</span>
                <button type="button" aria-label="Close details" className="ml-auto w-6 h-6 rounded-md flex items-center justify-center hover:bg-[#f0ece2]" style={{ color: '#9a9080' }} onClick={() => setOverlay(null)}>
                  <FiX size={14} />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto cs-content-scroll p-3 space-y-2.5">
                {/* Same contract as the Minutes card: record-backed fills only,
                    ask about the rest, never guess. */}
                {missingFields.length > 0 && (
                  <div className="rounded-xl border p-3" style={{ borderColor: '#e2d9c8', background: '#fff' }}>
                    <div className="flex items-start gap-2">
                      <Icon name="ai" size={14} style={{ color: '#145f5f' }} />
                      <div className="min-w-0">
                        <div className="text-[11.5px] font-semibold text-[#08111f]">
                          {missingFields.length} detail{missingFields.length === 1 ? '' : 's'} still blank
                        </div>
                        <div className="text-[10.5px] leading-relaxed mt-[2px]" style={{ color: '#7a7060' }}>
                          Vee can fill what your company record already answers — and will ask you about the rest rather than guess.
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="w-full mt-2.5" leftIcon={<Icon name="ai" size={13} />} onClick={askVeeToFillGaps}>
                      Let Vee fill the gaps
                    </Button>
                  </div>
                )}
                <StudioReportSections flashKeys={bridge.flashKeys} />

                {/* No Generate here — the form feeds Vee; drafting is Vee's job
                    (top-bar AI Polish stays for the direct path). */}
                <div className="rounded-xl border p-3" style={{ borderColor: '#cfe0dd', background: 'rgba(20,95,95,0.05)' }}>
                  <div className="text-[11.5px] leading-relaxed" style={{ color: '#145f5f' }}>
                    All set? Hand it to Vee — every detail on this form rides along automatically.
                  </div>
                  <Button size="sm" className="w-full mt-2" leftIcon={<FiMessageSquare size={13} />} onClick={() => setOverlay('dock')}>
                    Ask Vee to draft
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
